import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);
    
    // Log to analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }

    this.setState({
      error,
      errorInfo
    });

    // Check if this is a chunk loading error and try to reload
    if (error.message?.includes('Loading chunk') || error.message?.includes('ChunkLoadError')) {
      console.log('📦 Chunk load error detected, preparing auto-reload...');
      // Auto-reload after 3 seconds for chunk errors
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error?.message?.includes('Loading chunk') || 
                          this.state.error?.message?.includes('ChunkLoadError');

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full p-6 text-center space-y-4">
            <div className="flex justify-center">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {isChunkError ? 'Mise à jour en cours...' : 'Une erreur est survenue'}
              </h2>
              <p className="text-muted-foreground text-sm">
                {isChunkError 
                  ? 'L\'application se recharge automatiquement avec la dernière version.'
                  : 'Nous nous excusons pour ce désagrément. Veuillez réessayer.'
                }
              </p>
            </div>

            {isChunkError ? (
              <div className="flex items-center justify-center space-x-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Rechargement dans 3 secondes...</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Button onClick={this.handleReload} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Recharger la page
                </Button>
                <Button variant="outline" onClick={this.handleRetry} className="w-full">
                  Réessayer
                </Button>
              </div>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left text-xs text-muted-foreground mt-4">
                <summary className="cursor-pointer">Détails de l'erreur (dev)</summary>
                <pre className="mt-2 whitespace-pre-wrap overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;